import React, { useEffect, useState } from 'react';
import { Modal, ModalContent, ModalHeader, ModalBody, Input, Button, Chip } from '@nextui-org/react';
import { API_URL } from '../utils/constants';
import auth from '../Auth';
import { Search, Loader2, Play, Plus, Calendar } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const PlaygroundSelector = ({ isOpen, onClose }) => {
  const [prompts, setPrompts] = useState([]);
  const [filteredPrompts, setFilteredPrompts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [recentPrompts, setRecentPrompts] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    if (isOpen) {
      fetchPrompts();
      // Load recent prompts from localStorage
      try {
        const storedRecent = localStorage.getItem('recentPrompts');
        if (storedRecent) {
          setRecentPrompts(JSON.parse(storedRecent));
        } else {
          setRecentPrompts([]);
        }
      } catch (error) {
        console.error('Error loading recent prompts:', error);
        setRecentPrompts([]);
      }
    }
  }, [isOpen]);

  useEffect(() => {
    if (searchQuery) {
      const filtered = prompts.filter(prompt => 
        prompt.prompt_id.toLowerCase().includes(searchQuery.toLowerCase())
      );
      setFilteredPrompts(filtered);
    } else {
      setFilteredPrompts(prompts);
    }
  }, [searchQuery, prompts]);

  const fetchPrompts = async () => {
    setLoading(true);
    try {
      const token = auth.syncGetIdToken();
      const response = await fetch(`${API_URL}/prompts`, {
        headers: {
          Authorization: 'Bearer ' + token,
          'Content-Type': 'application/json'
        }
      });
      const data = await response.json();
      setPrompts(data);
      setFilteredPrompts(data);
    } catch (error) {
      console.error('Error fetching prompts:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePromptSelect = (promptId) => {
    // Update recent prompts
    updateRecentPrompts(promptId);
    
    // Navigate to the playground
    window.open(`/playground/${promptId}`);
    // onClose();
  };

  const updateRecentPrompts = (promptId) => {
    const prompt = prompts.find(p => p.prompt_id === promptId);
    if (!prompt) return;
    
    try {
      // Get the current list of recent prompts
      let recentList = [];
      const storedRecent = localStorage.getItem('recentPrompts');
      if (storedRecent) {
        recentList = JSON.parse(storedRecent);
      }
      
      // Remove the prompt if it's already in the list
      recentList = recentList.filter(p => p.prompt_id !== promptId);
      
      // Add the prompt to the beginning of the list
      recentList.unshift(prompt);
      
      // Keep only the 5 most recent prompts
      if (recentList.length > 5) {
        recentList = recentList.slice(0, 5);
      }
      
      // Save the updated list
      localStorage.setItem('recentPrompts', JSON.stringify(recentList));
      setRecentPrompts(recentList);
    } catch (error) {
      console.error('Error updating recent prompts:', error);
    }
  };

  const handleCreateNewPrompt = () => {
    window.open('/prompt/new');
    // onClose();
  };

  function formatDateTime(input) {
    if (!input || input.length <= 5) return input;
    
    try {
      const date = new Date(input);
      
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      
      return `${hours}:${minutes} ${day}.${month}.${year}`;
    } catch {
      return input;
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="2xl">
      <ModalContent>
        <ModalHeader className="flex flex-col gap-1">
          Select Prompt for Playground
        </ModalHeader>
        <ModalBody className="pb-6">
          <div className="flex items-center mb-4">
            <div className="flex-1 flex items-center border rounded-md p-2">
              <Search className="mr-2 text-gray-500" size={20} />
              <Input
                placeholder="Search prompts..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="border-0 outline-none focus:ring-0 w-full"
                aria-label="Search prompts"
                variant="bordered"
                size="sm"
              />
            </div>
            <Button 
              style={{ backgroundColor: '#000000', padding: '0.5rem 0.7rem', borderRadius: '0.4rem', color: 'white' }}
              className="ml-2"
              // startContent={<Plus size={16} />}
              onClick={handleCreateNewPrompt}
            >
              Create Prompt
            </Button>
          </div>
          
          {/* Recent prompts section */}
          {recentPrompts.length > 0 && !searchQuery && (
            <div className="mb-2">
              <div className="flex items-center mb-2">
                <Calendar size={16} className="mr-2 text-gray-500" />
                <h3 className="text-sm font-medium text-gray-700">Recent Prompts</h3>
              </div>
              <div className="flex flex-wrap gap-2">
                {recentPrompts.map(prompt => (
                  <Chip
                    key={prompt.prompt_id}
                    onClick={() => handlePromptSelect(prompt.prompt_id)}
                    className="cursor-pointer hover:bg-gray-100"
                    // startContent={<Play size={12} />}
                    variant="flat"
                  >
                    {prompt.prompt_id}
                  </Chip>
                ))}
              </div>
            </div>
          )}
          
          {loading ? (
            <div className="flex justify-center items-center h-40">
              <Loader2 className="animate-spin" size={40} />
            </div>
          ) : (
            <div className="max-h-72 overflow-y-auto">
              {filteredPrompts.length > 0 ? (
                <ul className="divide-y divide-gray-200">
                  {filteredPrompts.map((prompt) => (
                    <li 
                      key={prompt.prompt_id} 
                      className="py-3 flex justify-between items-center hover:bg-gray-50 cursor-pointer px-3 rounded transition-colors"
                      onClick={() => handlePromptSelect(prompt.prompt_id)}
                    >
                      <div className="flex-1">
                        <p className="text-sm font-medium text-gray-900">{prompt.prompt_id}</p>
                        <p className="text-xs text-gray-500">
                          Version: {formatDateTime(prompt.version)}
                        </p>
                      </div>
                        {/* <Play size={20} /> */}
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="text-center py-10 text-gray-500">
                  {searchQuery ? (
                    <p>No prompts found matching "{searchQuery}"</p>
                  ) : (
                    <div className="flex flex-col items-center">
                      <p className="mb-4">No prompts found. Create a prompt first.</p>
                      <Button 
                        style={{ backgroundColor: '#000000', padding: '0.5rem 0.7rem', borderRadius: '0.4rem', color: 'white' }}
                        // startContent={<Plus size={16} />}
                        onClick={handleCreateNewPrompt}
                      >
                        Create Prompt
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default PlaygroundSelector;